import type { CreateNoteInput } from '@keep/shared';
import { ImagePlus, SquareCheckBig } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useCreateNote } from '../../api/notes';
import { IconButton } from '../../components/ui/icon-button';
import { COMPOSE_EVENT, type ComposeEventDetail } from '../../hooks/use-keyboard-shortcuts';
import { errorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { noteColorVar } from '../../lib/colors';
import { toastError } from '../../stores/toasts';
import { emptyDraft, isDraftEmpty, toggleChecklist, type NoteDraft } from './draft';
import { NoteChips } from './note-chips';
import { NoteForm } from './note-form';
import { NoteToolbar } from './note-toolbar';
import { useImageUpload } from './use-image-upload';

function toCreateInput(draft: NoteDraft): CreateNoteInput {
  return {
    title: draft.title,
    content: draft.content,
    type: draft.type,
    items: draft.items
      .filter((item) => item.text.trim() !== '')
      .map(({ id, text, checked }) => ({ id, text, checked })),
    color: draft.color,
    labelIds: draft.labelIds,
    reminderAt: draft.reminderAt,
    pinned: draft.pinned,
    archived: draft.archived,
  };
}

/** Clic en dehors du compositeur… sauf dans un menu/popover qu'il a lui-même ouvert (rendu en portail). */
function isInsidePortal(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(target.closest('[data-radix-popper-content-wrapper], [role="dialog"]'))
  );
}

/**
 * « Créer une note… » : la note n'est créée qu'à la fermeture, et seulement si elle n'est pas vide.
 * Dans la vue d'un libellé ou des rappels, la nouvelle note hérite du contexte (comme dans Keep).
 */
export function NoteComposer({ defaults }: { defaults?: Partial<NoteDraft> }) {
  const create = useCreateNote();
  const uploadImages = useImageUpload();
  const [, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<NoteDraft>(() => emptyDraft(defaults));
  const container = useRef<HTMLDivElement>(null);
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const change = (patch: Partial<NoteDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const open = useCallback((type: 'text' | 'checklist') => {
    setDraft(emptyDraft({ ...defaultsRef.current, type }));
    setExpanded(true);
  }, []);

  const close = useCallback(
    (overrides: Partial<NoteDraft> = {}) => {
      const final = { ...draft, ...overrides };
      if (!isDraftEmpty(final)) {
        create.mutate(toCreateInput(final), {
          onError: (error) => toastError(errorMessage(error)),
        });
      }
      setExpanded(false);
      setDraft(emptyDraft(defaultsRef.current));
    },
    [draft, create],
  );

  // Fermeture par clic extérieur.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      if (container.current?.contains(event.target as Node) || isInsidePortal(event.target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded, close]);

  // Raccourcis clavier « c » et « l ».
  useEffect(() => {
    const onCompose = (event: Event) =>
      open((event as CustomEvent<ComposeEventDetail>).detail.type);
    window.addEventListener(COMPOSE_EVENT, onCompose);
    return () => window.removeEventListener(COMPOSE_EVENT, onCompose);
  }, [open]);

  /** Une image nécessite une note existante : on la crée tout de suite puis on l'ouvre dans l'éditeur. */
  const addImages = (files: File[]) => {
    create.mutate(toCreateInput(draft), {
      onSuccess: (note) => {
        uploadImages(note.id, files);
        setExpanded(false);
        setDraft(emptyDraft(defaultsRef.current));
        setParams((current) => {
          const next = new URLSearchParams(current);
          next.set('note', note.id);
          return next;
        });
      },
      onError: (error) => toastError(errorMessage(error)),
    });
  };

  const imageInput = useRef<HTMLInputElement>(null);

  if (!expanded) {
    return (
      <div className="mx-auto mt-8 mb-4 flex h-12 w-full max-w-[600px] items-center rounded-lg border border-line bg-bg pr-2 shadow-raised dark:border-line">
        <button
          type="button"
          className="h-full flex-1 px-4 text-left text-base font-medium text-muted"
          onClick={() => open('text')}
        >
          Créer une note…
        </button>
        <IconButton
          icon={SquareCheckBig}
          label="Nouvelle liste"
          onClick={() => open('checklist')}
        />
        <IconButton
          icon={ImagePlus}
          label="Nouvelle note avec image"
          onClick={() => imageInput.current?.click()}
        />
        <input
          ref={imageInput}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length) addImages(files);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={container}
      data-note-form
      style={{ backgroundColor: noteColorVar(draft.color) }}
      className={cn(
        'mx-auto mt-8 mb-4 w-full max-w-[600px] rounded-lg border shadow-raised',
        draft.color === 'default' ? 'border-line' : 'border-transparent',
      )}
      onKeyDown={(event) => {
        if (event.key === 'Escape' || (event.key === 'Enter' && (event.ctrlKey || event.metaKey))) {
          event.preventDefault();
          close();
        }
      }}
    >
      <NoteForm draft={draft} onChange={change} autoFocus="content" compact />
      <NoteChips
        className="px-4 pb-2"
        note={draft}
        onRemoveReminder={() => change({ reminderAt: null })}
        onRemoveLabel={(labelId) =>
          change({ labelIds: draft.labelIds.filter((id) => id !== labelId) })
        }
      />
      <div className="flex items-center justify-between px-2 py-1.5">
        <NoteToolbar
          color={draft.color}
          reminderAt={draft.reminderAt}
          labelIds={draft.labelIds}
          archived={draft.archived}
          type={draft.type}
          trashed={false}
          onReminder={(reminderAt) => change({ reminderAt })}
          onColor={(color) => change({ color })}
          onAddImages={addImages}
          onArchive={() => close({ archived: true, pinned: false })}
          onToggleLabel={(labelId, enabled) =>
            change({
              labelIds: enabled
                ? [...draft.labelIds, labelId]
                : draft.labelIds.filter((id) => id !== labelId),
            })
          }
          onToggleChecklist={() => change(toggleChecklist(draft))}
        />
        <button
          type="button"
          onClick={() => close()}
          className="h-9 shrink-0 rounded px-6 text-sm font-medium hover:bg-hover"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}
